/**
 * UI 命令
 * 启动 Workflow Web UI 界面
 */

import { Command } from 'commander';
import chalk from 'chalk';
import ora from 'ora';
import {
  isPortAvailable,
  isApiServerRunning,
  startApiServer,
  startWebUIServer,
  openBrowser,
  setupGracefulShutdown,
  ServerProcess,
} from '../utils/ui-manager.js';
import { apiConfig } from '../api/config.js';
import { getApiServer } from '../api/server.js';
import { logger } from '../utils/logger.js';

export function createUICommand(): Command {
  const command = new Command('ui');
  
  command
    .description('Launch the full-featured Web UI (Kanban, Flowgram, Agent, PRD editor, etc.)')
    .option('--api-port <port>', 'API server port', apiConfig.port.toString())
    .option('--ui-port <port>', 'UI server port', '5173')
    .option('--no-open', 'Do not open browser automatically')
    .option('--api-only', 'Start API server only')
    .option('--ui-only', 'Start UI server only (requires API server running)')
    .action(async (options) => {
      console.log(chalk.blue.bold('\n🚀 Workflow Web UI - Full Visual Platform\n'));
      
      const apiPort = parseInt(options.apiPort);
      const uiPort = parseInt(options.uiPort);
      const servers: ServerProcess[] = [];
      
      try {
        // Step 1: Handle API server
        if (!options.uiOnly) {
          console.log(chalk.cyan('1️⃣  Checking API server...'));
          
          const isApiRunning = await isApiServerRunning(apiPort);
          
          if (isApiRunning) {
            console.log(chalk.green(`✓ API server already running on port ${apiPort}`));
          } else {
            // Check port availability
            const apiPortAvailable = await isPortAvailable(apiPort);
            if (!apiPortAvailable) {
              console.error(chalk.red(`✗ Port ${apiPort} is already in use`));
              console.log(chalk.yellow(`  Try: lsof -i :${apiPort} to see what's using it`));
              process.exit(1);
            }
            
            // Start API server
            const apiServer = await startApiServer(apiPort);
            servers.push(apiServer);
          }
        }
        
        // If API only mode, stop here
        if (options.apiOnly) {
          console.log(chalk.green.bold('\n✅ API server is ready!\n'));
          console.log(chalk.white('API Base URL: ') + chalk.blue.underline(`http://localhost:${apiPort}${apiConfig.basePath}/${apiConfig.version}`));
          console.log(chalk.white('Health Check: ') + chalk.blue.underline(`http://localhost:${apiPort}/health`));
          console.log();
          console.log(chalk.gray('Press Ctrl+C to stop the server'));
          console.log();
          
          setupGracefulShutdown(servers);
          await new Promise(() => {}); // Keep running
          return;
        }
        
        // Step 2: Check UI port
        console.log(chalk.cyan('\n2️⃣  Checking UI port availability...'));
        
        const uiPortAvailable = await isPortAvailable(uiPort);
        if (!uiPortAvailable) {
          console.error(chalk.red(`✗ Port ${uiPort} is already in use`));
          console.log(chalk.yellow(`  Try: lsof -i :${uiPort} to see what's using it`));
          
          // Clean up API server if we started it
          if (servers.length > 0) {
            for (const server of servers) {
              if (server.type === 'api') {
                await getApiServer().stop();
              }
            }
          }
          process.exit(1);
        }
        
        console.log(chalk.green(`✓ Port ${uiPort} is available`));
        
        // Step 3: Start Web UI server
        console.log(chalk.cyan('\n3️⃣  Starting Web UI server...'));
        
        const uiServer = await startWebUIServer(uiPort);
        servers.push(uiServer);
        
        // Step 4: Open browser
        const url = `http://localhost:${uiPort}`;
        
        if (options.open) {
          console.log(chalk.cyan('\n4️⃣  Opening browser...'));
          await openBrowser(url);
          console.log(chalk.green(`✓ Browser opened`));
        }
        
        // Success message
        console.log(chalk.green.bold('\n🎉 Workflow UI is ready!\n'));
        console.log(chalk.white('Web UI URL: ') + chalk.blue.underline(url));
        console.log(chalk.white('API Server: ') + chalk.blue(`http://localhost:${apiPort}`));
        
        console.log(chalk.cyan('\n✨ Available features:'));
        console.log('  • 📋 Kanban Board - Drag & drop task management');
        console.log('  • 🔀 Flowgram - Visual process flow editor');
        console.log('  • 🤖 Agent System - AI assistant management');
        console.log('  • 📄 PRD Editor - Product requirement documents');
        console.log('  • 💡 Prompt Editor - AI prompt engineering');
        
        if (!options.open) {
          console.log(chalk.yellow('\nOpen your browser and visit the URL above'));
        }
        
        console.log();
        console.log(chalk.gray('Press Ctrl+C to stop all servers'));
        console.log();
        
        // Setup graceful shutdown
        setupGracefulShutdown(servers);
        
        // Keep the process running
        await new Promise(() => {});
        
      } catch (error: any) {
        console.error(chalk.red('\n❌ Failed to start:'), error.message);
        
        // Clean up any started servers
        for (const server of servers) {
          try {
            if (server.type === 'api') {
              await getApiServer().stop();
            } else if (server.process) {
              server.process.kill();
            }
          } catch {}
        }
        
        process.exit(1);
      }
    });
  
  // Sub-command: check status
  command
    .command('status')
    .description('Check UI and API server status')
    .action(async () => {
      console.log(chalk.blue.bold('\n📊 Server Status\n'));
      
      const apiPort = apiConfig.port;
      const uiPort = 5173;
      
      // Check API server
      const apiRunning = await isApiServerRunning(apiPort);
      if (apiRunning) {
        console.log(chalk.green(`✓ API server is running on port ${apiPort}`));
        console.log(chalk.gray(`  ${apiConfig.basePath}/${apiConfig.version}`));
      } else {
        console.log(chalk.yellow(`✗ API server is not running`));
      }
      
      // Check UI server (simple port check)
      const uiPortInUse = !await isPortAvailable(uiPort);
      if (uiPortInUse) {
        console.log(chalk.green(`✓ UI server might be running on port ${uiPort}`));
        console.log(chalk.gray(`  http://localhost:${uiPort}`));
      } else {
        console.log(chalk.yellow(`✗ UI server is not running`));
      }
      
      console.log();
    });
  
  return command;
}